/**
 * Focus trap utilities — keep keyboard focus inside a container while it is
 * "active" (drawer/popup open). Wraps focus on Tab from last → first and
 * Shift+Tab from first → last.
 *
 * Loaded as ES module via `@theme/focus-trap` importmap entry.
 *
 * Usage:
 *   trapFocus(container)            // start trap
 *   removeTrapFocus(elementToFocus) // stop trap, optionally restore focus
 *
 * Internal handler state is module-scoped, so only one trap can be active
 * at a time (matches the original global.js behavior).
 */

export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe, .focusable"
    )
  );
}

const trapFocusHandlers = {};

export function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (first) {
    first.focus();
  } else {
    elementToFocus.focus();
  }
}

export function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;
window.getFocusableElements = getFocusableElements;
